import { useState, useEffect } from "react";
import axios from "axios";
import { X, Trophy, Calendar, Users, Star, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tier badge colors
const TIER_COLORS = {
  free: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  pro: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  pro_plus: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  pro_max: "bg-amber-500/20 text-amber-400 border-amber-500/30"
};

const TIER_LABELS = {
  free: "Free",
  pro: "Pro",
  pro_plus: "Pro+",
  pro_max: "Pro Max"
};

export default function UserProfileCard({ userId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/users/${userId}/profile`);
        setProfile(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  if (!userId) return null;

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Generate a consistent color based on user ID
  const getAvatarColor = (id) => {
    const colors = [
      "bg-blue-500", "bg-emerald-500", "bg-purple-500", 
      "bg-amber-500", "bg-rose-500", "bg-cyan-500"
    ];
    const hash = id ? id.charCodeAt(0) + id.charCodeAt(id.length - 1) : 0;
    return colors[hash % colors.length];
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="user-profile-modal"
    >
      <div 
        className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-slate-700 to-slate-800 p-6 pb-8">
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors"
            data-testid="close-profile-btn"
          >
            <X size={20} />
          </button>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <p className="text-red-400">{error}</p>
            </div>
          ) : profile && (
            <div className="text-center">
              {/* Avatar */}
              <div className={`w-20 h-20 mx-auto rounded-full ${getAvatarColor(userId)} flex items-center justify-center text-white text-2xl font-bold mb-3`}>
                {getInitials(profile.display_name || profile.team_name)}
              </div>
              
              {/* Person's Name (large) */}
              <h3 className="text-white text-xl font-bold">
                {profile.display_name || "Unknown"}
              </h3>
              
              {/* Team Name (below) */}
              <p className="text-slate-400 text-sm mt-1">
                {profile.team_name || "No team"}
              </p>
              
              {/* Tier Badge */}
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold border ${TIER_COLORS[profile.tier] || TIER_COLORS.free}`}>
                {TIER_LABELS[profile.tier] || "Free"}
              </span>
            </div>
          )}
        </div>

        {/* Stats */}
        {!loading && !error && profile && (
          <div className="p-4 space-y-3">
            {/* Rank & Points */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-amber-400 mb-1">
                  <Trophy size={16} />
                  <span className="text-lg font-bold">#{profile.global_rank}</span>
                </div>
                <p className="text-slate-500 text-xs">Global Rank</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1 text-emerald-400 mb-1">
                  <Star size={16} />
                  <span className="text-lg font-bold">{profile.total_points}</span>
                </div>
                <p className="text-slate-500 text-xs">Total Points</p>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-900/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-300">
                  <Calendar size={14} className="text-slate-500" />
                  <span className="text-sm">{profile.matches_played} matches</span>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-300">
                  <Users size={14} className="text-slate-500" />
                  <span className="text-sm">{profile.leagues_count} leagues</span>
                </div>
              </div>
            </div>

            {/* Member Since */}
            <div className="text-center text-slate-500 text-xs pt-2 border-t border-slate-700">
              Member since {profile.member_since || "Unknown"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
